import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Divider,
    IconButton,
    Paper,
    Popover,
    Stack,
    Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { fns } from "../helpers/common";
import { CATEGORY, findOccurrence, getRecurrenceType } from "../helpers/events";
import { dayjsUTC } from "../helpers/dates";

const dayjs = dayjsUTC;

export const getPreviousEventFields = (event, metrics = null) => [
    {
        label: "Category",
        value: CATEGORY[event?.Category]?.label ?? event?.Category ?? null,
        format: null,
    },
    { label: "Subcategory", value: event?.Subcategory || null },
    {
        label: "Planned Budget",
        value: event?.PlannedBudget ?? null,
        format: { left: "currency" },
    },
    {
        label: "Actual Spend",
        value: event?.ActualSpend ?? null,
        format: { left: "currency" },
    },
    {
        label: "Conversions / Bookings",
        value: metrics?.bookingsRooms ?? null,
    },
    { label: "Room Nights", value: metrics?.roomNightsTotal ?? null },
    {
        label: "Revenue Generated",
        value: metrics?.revenueTotal ?? null,
        format: { left: "currency" },
    },
];

export const getSummaryFields = (event) => {
    const isRecurring = Boolean(event?.RepeatRule);
    const occurrenceType = isRecurring
        ? getRecurrenceType(event.RepeatRule)
        : null;
    const occurrence = isRecurring
        ? findOccurrence(
              event.RepeatRule,
              event.FromDate,
              event.ToDate,
              event.RepeatStart,
          )
        : null;

    return [
        { label: "Name", value: event?.Name ?? null },
        {
            label: "Start Date",
            value: event?.FromDate
                ? dayjs(event.FromDate).format("D MMM YYYY")
                : null,
        },
        {
            label: "End Date",
            value: event?.ToDate
                ? dayjs(event.ToDate).format("D MMM YYYY")
                : null,
        },
        ...(isRecurring
            ? [
                  { label: "Occurrence Type", value: occurrenceType },
                  {
                      label: "Occurrence Count",
                      value: occurrence?.index ?? null,
                  },
              ]
            : []),
        {
            label: "Category",
            value: CATEGORY[event?.Category]?.label ?? event?.Category ?? null,
        },
        {
            label: "Subcategory",
            value: event?.Subcategory || null,
        },
        {
            label: "Description",
            value: event?.Description || null,
            scrollable: true,
        },
        {
            label: "Planned Budget",
            value: event?.PlannedBudget || null,
            format: { left: "currency" },
        },
        {
            label: "Actual Spend",
            value: event?.ActualSpend || null,
            format: { left: "currency" },
        },
    ];
};

const FIELDS_BY_MODE = {
    performance: getPreviousEventFields,
    summary: getSummaryFields,
};

// mode="performance" — performance fields + info icon that opens Event Summary popper
// mode="summary"     — summary fields, no info icon, usable standalone anywhere
// metrics consists of extra metrics data that is used to populate the performance fields, if available
const EventsSummaryCard = ({
    mode,
    title,
    event,
    metrics = null,
    headerExtra = null,
}) => {
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const [anchorEl, setAnchorEl] = useState(null);

    const fields = (FIELDS_BY_MODE[mode] ?? (() => []))(event, metrics);
    const isPerformance = mode === "performance";

    const formatValue = (value, format) => {
        if (value == null) return "-";
        if (format == null) return String(value);
        return fns(hotelID, value, format);
    };

    const renderRows = (rowFields, truncate = false) => (
        <Stack
            spacing={2}
            direction="column"
            divider={<Divider flexItem sx={{ borderColor: "grey.300" }} />}
        >
            {rowFields.map(({ label, value, format, scrollable }) => (
                <Stack
                    key={label}
                    direction="row"
                    justifyContent="space-between"
                    alignItems={scrollable ? "flex-start" : "center"}
                >
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ flexShrink: 0, mr: 2 }}
                    >
                        {label}
                    </Typography>
                    {scrollable ? (
                        <Box
                            sx={{
                                maxWidth: "60%",
                                maxHeight: 80,
                                overflowY: "auto",
                                textAlign: "right",
                            }}
                        >
                            <Typography
                                variant="body1"
                                fontWeight="bold"
                                sx={{ wordBreak: "break-word" }}
                            >
                                {formatValue(value, format)}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            sx={{
                                maxWidth: "60%",
                                textAlign: "right",
                                ...(truncate
                                    ? {
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                      }
                                    : { wordBreak: "break-word" }),
                            }}
                        >
                            {formatValue(value, format)}
                        </Typography>
                    )}
                </Stack>
            ))}
        </Stack>
    );

    return (
        <Paper elevation={3} sx={{ p: 2, height: "100%", borderRadius: 2 }}>
            {title && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1, height: 40 }}
                >
                    <Stack direction="row" alignItems="center">
                        <Typography variant="h6" fontWeight="bold">
                            {title}
                        </Typography>
                        {isPerformance && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    if (!anchorEl) setAnchorEl(e.currentTarget);
                                }}
                            >
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>
                    {headerExtra}
                </Stack>
            )}
            {title && <br />}

            {renderRows(fields, isPerformance)}

            {isPerformance && (
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                >
                    <Box sx={{ p: 2, minWidth: 400, maxWidth: 800 }}>
                        <Typography variant="h6" fontWeight="bold">
                            Event Summary
                        </Typography>
                        <br />
                        {renderRows(getSummaryFields(event))}
                    </Box>
                </Popover>
            )}
        </Paper>
    );
};

export default EventsSummaryCard;
